import { useState, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight';
import { APP_VERSION, BUILD_DATE, COPYRIGHT_YEAR, COMPANY_NAME } from '../../../config/version';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('intelligenceSidebarCollapsed');
    return saved === 'true';
  });
  const location = useLocation();
  const isChatRoute = location.pathname === '/intelligence/chat';

  // While the on-screen keyboard is open on the chat page, shrink the whole
  // shell to the visible viewport so the composer sits on the keyboard and
  // the app bar stays on screen (iOS pans instead of resizing — see hook).
  const keyboardHeight = useVisualViewportHeight(isChatRoute);

  // Auto-collapse sidebar on chat page for more space
  useEffect(() => {
    if (isChatRoute) {
      setSidebarCollapsed(true);
    }
  }, [isChatRoute]);

  // Save collapsed state to localStorage
  const handleToggleCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('intelligenceSidebarCollapsed', String(newState));
  };

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-gray-50 supports-[height:100dvh]:h-dvh dark:bg-gray-900"
      style={keyboardHeight ? { height: keyboardHeight } : undefined}
    >
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />
        {/* The chat page manages its own full-height layout (pinned composer,
            inner scrolling), so it gets the untouched flex cell; every other
            page keeps the padded scroll container. */}
        {isChatRoute ? (
          <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
            {children}
          </main>
        ) : (
          <main className="flex-1 overflow-auto p-4">
            {children}
          </main>
        )}
      </div>
      <footer
        className={`flex-shrink-0 bg-white border-t border-gray-200 py-2 px-6 dark:bg-gray-800 dark:border-gray-700 ${
          isChatRoute ? 'hidden md:block' : ''
        }`}
      >
        <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-1 dark:text-gray-400">
          <p>&copy; {COPYRIGHT_YEAR} {COMPANY_NAME}. All rights reserved.</p>
          <p>Version {APP_VERSION} &middot; {BUILD_DATE}</p>
        </div>
      </footer>
    </div>
  );
}
